import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

interface Props {
    forProduction: boolean;
    onEdit: () => void;
    onDuplicate: () => void;
    onViewUsage: () => void;
    onAddToWarehouse: () => void;
    onDelete: () => void;
}

const I = ({ d }: { d: string }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d={d} />
    </svg>
);

// Approximate popover height: 5 items × ~38px + padding/dividers + buffer.
const POPOVER_HEIGHT = 230;

const ArticleActionMenu: React.FC<Props> = ({ forProduction, onEdit, onDuplicate, onViewUsage, onAddToWarehouse, onDelete }) => {
    const [open, setOpen] = useState(false);
    const [placement, setPlacement] = useState<'top' | 'bottom'>('bottom');
    const ref = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const openMenu = () => {
        // Flip the popover above the trigger when there isn't room below —
        // matters most on mobile where the bottom rows would push the menu
        // off-screen otherwise.
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            setPlacement(spaceBelow < POPOVER_HEIGHT && spaceAbove > spaceBelow ? 'top' : 'bottom');
        }
        setOpen(true);
    };

    const click = (action: () => void) => (e: React.MouseEvent) => {
        e.stopPropagation();
        setOpen(false);
        action();
    };

    return (
        <div className="art-action-menu" ref={ref} onClick={(e) => e.stopPropagation()}>
            <button
                ref={triggerRef}
                type="button"
                className="art-action-trigger"
                aria-label="Veprime"
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={(e) => { e.stopPropagation(); if (open) { setOpen(false); } else { openMenu(); } }}
            >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <circle cx="12" cy="5" r="1.6" />
                    <circle cx="12" cy="12" r="1.6" />
                    <circle cx="12" cy="19" r="1.6" />
                </svg>
            </button>
            {open && (
                <div className={`art-action-popover art-action-popover--${placement}`} role="menu">
                    <button type="button" role="menuitem" className="art-action-item" onClick={click(onEdit)}>
                        <I d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        <FormattedMessage id="admin.articles.action.edit" defaultMessage="Edito" />
                    </button>
                    <button type="button" role="menuitem" className="art-action-item" onClick={click(onDuplicate)}>
                        <I d="M16 1H4a2 2 0 0 0-2 2v12h2V3h12V1zm3 4H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2zm0 16H8V7h11v14z" />
                        <FormattedMessage id="admin.articles.action.duplicate" defaultMessage="Dupliko" />
                    </button>
                    <button type="button" role="menuitem" className="art-action-item" onClick={click(onViewUsage)}>
                        <I d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" />
                        <FormattedMessage id="admin.articles.action.viewUsage" defaultMessage="Shiko ku përdoret" />
                    </button>
                    {forProduction === false && (
                        <button type="button" role="menuitem" className="art-action-item" onClick={click(onAddToWarehouse)}>
                            <I d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                            <FormattedMessage id="admin.articles.action.addToWarehouse" defaultMessage="Shto në depo" />
                        </button>
                    )}
                    <div className="art-action-divider" />
                    <button type="button" role="menuitem" className="art-action-item art-action-item--danger" onClick={click(onDelete)}>
                        <I d="M3 6h18 M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        <FormattedMessage id="admin.articles.action.delete" defaultMessage="Fshi" />
                    </button>
                </div>
            )}
        </div>
    );
};

export default ArticleActionMenu;
